export interface RecoveryToastOptions {
	timeOut: number;
	extendedTimeOut: number;
	progressBar: boolean;
	closeButton: boolean;
	tapToDismiss: boolean;
	onclick: () => void;
	onCloseClick: () => void;
	onHidden: () => void;
}

const RELOAD_KEY = 'ff-stale-chunk-reload';
const AUTO_RELOAD_TIMEOUT = 8000;

/** Keeps a failed reload from looping, but allows recovery again after navigation succeeds. */
export class StaleChunkRecovery {
	private active: { cancelled: boolean; toast?: unknown; timer?: ReturnType<typeof setTimeout> };

	constructor(
		private storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
		private showWarning: (options: RecoveryToastOptions) => unknown,
		private dismiss: (toast: unknown) => void,
		private reload: () => void
	) {}

	start() {
		if (this.active || this.storage.getItem(RELOAD_KEY)) return;
		this.storage.setItem(RELOAD_KEY, '1');
		const incident: { cancelled: boolean; toast?: unknown; timer?: ReturnType<typeof setTimeout> } = { cancelled: false };
		this.active = incident;
		let reloadRequested = false;
		const reload = () => {
			if (!incident.cancelled && !reloadRequested) {
				reloadRequested = true;
				if (incident.timer) clearTimeout(incident.timer);
				this.reload();
			}
		};
		// Toastr's onHidden callback is not reliable when its fade-out is interrupted or
		// the tab is backgrounded. Keep the reload decision in this class as well.
		incident.timer = setTimeout(reload, AUTO_RELOAD_TIMEOUT);
		incident.toast = this.showWarning({
			timeOut: AUTO_RELOAD_TIMEOUT,
			extendedTimeOut: 4000,
			progressBar: true,
			closeButton: true,
			tapToDismiss: false,
			onclick: reload,
			onCloseClick: () => {
				incident.cancelled = true;
				if (incident.timer) clearTimeout(incident.timer);
				this.active = undefined;
				this.storage.removeItem(RELOAD_KEY);
			},
			onHidden: reload,
		});
	}

	complete() {
		// NavigationEnd proves the app recovered. Cancel the old toast before dismissing it,
		// because toastr invokes onHidden even when a toast is dismissed programmatically.
		this.storage.removeItem(RELOAD_KEY);
		if (this.active) {
			this.active.cancelled = true;
			if (this.active.timer) clearTimeout(this.active.timer);
			this.dismiss(this.active.toast);
			this.active = undefined;
		}
	}
}
